import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RECENTS_KEY = 'resourcesCenter.recentlyVisited';
const MAX_RECENTS = 8;

// (label, icon, substring to match against a resource's title — case-insensitive)
const QUICK_ACCESS_DEFS = [
  { key: 'case-status', label: 'Case Status', icon: '🔍', match: 'uscis case status', subtitle: 'Check your status' },
  { key: 'processing-times', label: 'Processing Times', icon: '⏱️', match: 'uscis processing times', subtitle: 'Check estimates' },
  { key: 'file-online', label: 'File Online', icon: '📝', match: 'uscis file online', subtitle: 'Start application' },
  { key: 'change-of-address', label: 'Change of Address', icon: '🏠', match: 'uscis change of address', subtitle: 'Update your record' },
  { key: 'i94', label: 'I-94 Record', icon: '🛂', match: 'cbp i-94', subtitle: 'Arrival / departure' },
];

const loadRecents = () => {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const SearchIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const ResourceCard = ({ resource, category, isLoggedIn, isSaved, onToggleSave, onOpen }) => {
  const icon = resource.icon || category?.icon || '🔗';
  return (
    <div
      className={`relative flex flex-col bg-white rounded-2xl border transition hover:shadow-md hover:-translate-y-0.5 overflow-hidden ${
        resource.is_featured ? 'border-amber-300' : 'border-slate-200'
      }`}
    >
      <div className="p-5 flex flex-col flex-1">
        {isLoggedIn && (
          <button
            type="button"
            onClick={onToggleSave}
            aria-label={isSaved ? `Remove ${resource.title} from saved resources` : `Save ${resource.title}`}
            aria-pressed={isSaved}
            className={`absolute top-2 right-2 flex items-center justify-center w-11 h-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
              isSaved ? 'text-red-600' : 'text-slate-300 hover:text-red-400'
            }`}
          >
            <span className="text-xl leading-none" aria-hidden="true">{isSaved ? '♥' : '♡'}</span>
          </button>
        )}

        <div className="flex items-start gap-3 pr-9">
          <div className="flex items-center justify-center w-11 h-11 rounded-lg bg-blue-50 text-2xl shrink-0" aria-hidden="true">
            {icon}
          </div>
          <div className="min-w-0">
            <h4 className="font-bold text-navy-700 leading-tight">{resource.title}</h4>
            {resource.is_featured && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                <span aria-hidden="true">⭐</span> Featured
              </span>
            )}
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-3 flex-1">
          {resource.description || ' '}
        </p>

        {(resource.is_official || resource.agency) && (
          <div className="flex items-center gap-2 mt-3">
            {resource.is_official && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 shrink-0">
                <span aria-hidden="true">✓</span> Official
              </span>
            )}
            {resource.agency && <span className="text-xs text-slate-500 truncate">{resource.agency}</span>}
          </div>
        )}
      </div>

      <a
        href={resource.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onOpen}
        aria-label={`Open ${resource.title} (opens in new tab)`}
        className="mx-5 mb-5 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
      >
        Open Resource
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </a>
    </div>
  );
};

const ResourcesCenter = () => {
  const navigate = useNavigate();

  // Lightweight, self-contained auth detection — this is a public top-level
  // route (like LandingPage/ProviderSearch), so it isn't wrapped by
  // AuthProvider's Dashboard/Header components. It mirrors exactly how
  // AuthProvider.fetchUserProfile() in App.js attaches the token.
  const [token] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, [token]);

  const [categories, setCategories] = useState([]);
  const [allResources, setAllResources] = useState([]); // unfiltered — powers Quick Access
  const [displayResources, setDisplayResources] = useState([]); // filtered — powers the grouped listing
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Flips true only once the live active-resources catalog has successfully
  // loaded — used to gate Recently Visited filtering/pruning so we never
  // wipe the cache before we actually know what's still active.
  const [resourcesLoaded, setResourcesLoaded] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(''); // category slug, '' = All

  // Debounce the search box ~300ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Initial load: categories + the full unfiltered resource list.
  useEffect(() => {
    const loadInitial = async () => {
      setLoading(true);
      setError('');
      try {
        const [catRes, resRes] = await Promise.all([
          axios.get(`${API}/resources/categories`),
          axios.get(`${API}/resources`),
        ]);
        setCategories(catRes.data || []);
        setAllResources(resRes.data || []);
        setDisplayResources(resRes.data || []);
        setResourcesLoaded(true);
      } catch (err) {
        console.error('Failed to load resources:', err);
        setError('Failed to load resources. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadInitial();
  }, []);

  // Re-fetch (server-side filtered) whenever search/category change, skipping
  // the very first render since loadInitial() already populated the list.
  const isFirstFilterRun = useRef(true);
  useEffect(() => {
    if (isFirstFilterRun.current) {
      isFirstFilterRun.current = false;
      return;
    }
    const fetchFiltered = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {};
        if (debouncedSearch) params.search = debouncedSearch;
        if (selectedCategory) params.category = selectedCategory;
        const res = await axios.get(`${API}/resources`, { params });
        setDisplayResources(res.data || []);
      } catch (err) {
        console.error('Failed to search resources:', err);
        setError('Failed to load resources. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchFiltered();
  }, [debouncedSearch, selectedCategory]);

  // Saved resources — only fetched/shown for a logged-in user.
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    if (!token || !user) return;
    axios
      .get(`${API}/me/saved-resources`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setSavedIds(new Set((res.data || []).map((r) => r.id))))
      .catch((err) => console.error('Failed to load saved resources:', err));
  }, [token, user]);

  const toggleSave = async (resource) => {
    if (!token) return;
    const isSaved = savedIds.has(resource.id);

    // Optimistic update.
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (isSaved) next.delete(resource.id);
      else next.add(resource.id);
      return next;
    });

    try {
      if (isSaved) {
        await axios.delete(`${API}/me/saved-resources/${resource.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API}/me/saved-resources/${resource.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch (err) {
      console.error('Failed to update saved resource:', err);
      // Revert on failure.
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (isSaved) next.add(resource.id);
        else next.delete(resource.id);
        return next;
      });
    }
  };

  // Recently visited (localStorage — works for anonymous users too).
  const [recents, setRecents] = useState(loadRecents);

  const addRecent = (resource) => {
    setRecents((prev) => {
      const filtered = prev.filter((r) => r.id !== resource.id);
      const next = [
        {
          id: resource.id,
          title: resource.title,
          url: resource.url,
          icon: resource.icon,
          agency: resource.agency,
          category_id: resource.category_id,
        },
        ...filtered,
      ].slice(0, MAX_RECENTS);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        // Ignore storage errors (e.g. private browsing quota).
      }
      return next;
    });
  };

  // Click tracking is fire-and-forget and never blocks opening the resource —
  // the anchors already carry target="_blank" rel="noopener noreferrer", so
  // this only records analytics + the recently-visited list.
  const handleResourceOpen = (resource) => {
    axios
      .post(`${API}/resources/${resource.id}/click`, { referrer_page: window.location.pathname })
      .catch(() => {});
    addRecent(resource);
  };

  // Drop any recently-visited entry whose underlying resource has since been
  // removed/deactivated (no longer in the live catalog). Match by id first
  // (the shape addRecent() always writes), falling back to slug then url for
  // any legacy/partial cache entries. We only do this once the live catalog
  // has actually loaded — otherwise every entry would look "missing" on a
  // fresh page load and we'd wipe a perfectly valid cache.
  const visibleRecents = useMemo(() => {
    if (!resourcesLoaded) return recents;
    return recents.filter((r) => {
      if (r.id !== undefined && r.id !== null) {
        return allResources.some((res) => res.id === r.id);
      }
      if (r.slug) {
        return allResources.some((res) => res.slug === r.slug);
      }
      if (r.url) {
        return allResources.some((res) => res.url === r.url);
      }
      return false;
    });
  }, [recents, allResources, resourcesLoaded]);

  // Self-heal: once we know which cached entries are stale, prune them out
  // of both state and localStorage so they don't linger across reloads.
  useEffect(() => {
    if (!resourcesLoaded) return;
    if (visibleRecents.length === recents.length) return;
    setRecents(visibleRecents);
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(visibleRecents));
    } catch {
      // Ignore storage errors (e.g. private browsing quota).
    }
  }, [resourcesLoaded, visibleRecents, recents]);

  const categoryById = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.id, c));
    return map;
  }, [categories]);

  const groupedResources = useMemo(() => {
    const byCategory = new Map();
    displayResources.forEach((r) => {
      if (!byCategory.has(r.category_id)) byCategory.set(r.category_id, []);
      byCategory.get(r.category_id).push(r);
    });
    return categories
      .map((cat) => ({ category: cat, resources: byCategory.get(cat.id) || [] }))
      .filter((group) => group.resources.length > 0);
  }, [categories, displayResources]);

  // Quick Access is always built off the full unfiltered list, so it stays
  // put regardless of search/category filtering.
  const quickAccessItems = useMemo(() => {
    return QUICK_ACCESS_DEFS.map((def) => {
      const resource = allResources.find((r) => r.title.toLowerCase().includes(def.match));
      return resource ? { ...def, resource } : null;
    }).filter(Boolean);
  }, [allResources]);

  const chipClass = (value) =>
    `inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
      selectedCategory === value
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:text-slate-900'
    }`;

  const showEmptyState = !loading && !error && groupedResources.length === 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-navy-700 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:font-semibold"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-gradient-to-r from-navy-700 to-blue-700 border-b border-blue-800">
        <div className="container mx-auto flex justify-between items-center px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 focus:outline-none focus:ring-2 focus:ring-white/50 rounded"
          >
            <div className="relative w-9 h-9 flex-shrink-0">
              <svg className="w-9 h-9" viewBox="0 0 36 36" fill="none">
                <path d="M18 2L4 8v10c0 8.5 6 16 14 18 8-2 14-9.5 14-18V8L18 2z" fill="#1e3a5f" />
                <path d="M18 5L7 10v8.5C7 25.5 12 31.5 18 33c6-1.5 11-7.5 11-14.5V10L18 5z" fill="#2563eb" />
                <circle cx="12" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="18" cy="11" r="1.2" fill="white" opacity="0.9" />
                <circle cx="24" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="15" cy="18" r="1.2" fill="white" opacity="0.9" />
                <circle cx="21" cy="18" r="1.2" fill="white" opacity="0.9" />
                <path d="M13 23l3.5 3.5L24 19" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-left">
              <span className="text-base font-bold leading-tight block text-white">My Immigration</span>
              <span className="text-xs font-semibold leading-tight block tracking-wide uppercase text-blue-200">Services</span>
            </div>
          </button>
          <nav aria-label="Resources navigation" className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/find-legal-help')}
              className="text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors font-medium text-sm"
            >
              Find Legal Help
            </button>
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors font-medium text-sm"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors font-medium text-sm"
              >
                Login
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1.5 text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back to Home
            </button>
          </nav>
        </div>
      </header>

      <main id="main-content" className="container mx-auto px-4 py-8">
        <div className="mb-6 text-center">
          <h2 className="text-3xl font-bold text-navy-700 mb-2">Resources</h2>
          <p className="text-slate-500">
            Find immigration tools, USCIS links, and helpful government resources.
          </p>
        </div>

        {/* Search + category filter */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-6 mb-8">
          <label htmlFor="resource-search" className="sr-only">
            Search resources
          </label>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
              <SearchIcon />
            </span>
            <input
              id="resource-search"
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search resources, forms, tools..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
            />
          </div>

          <div
            className="flex gap-2 mt-4 overflow-x-auto pb-1 -mx-1 px-1 sm:flex-wrap sm:overflow-visible"
            role="group"
            aria-label="Filter by category"
          >
            <button type="button" onClick={() => setSelectedCategory('')} aria-pressed={selectedCategory === ''} className={chipClass('')}>
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedCategory(c.slug)}
                aria-pressed={selectedCategory === c.slug}
                className={chipClass(c.slug)}
              >
                <span aria-hidden="true">{c.icon}</span> {c.name}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl mb-6 font-semibold" role="alert">
            {error}
          </div>
        )}

        {loading && displayResources.length === 0 && !error ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-500">Loading resources…</p>
          </div>
        ) : (
          <>
            {/* Quick Access */}
            {quickAccessItems.length > 0 && (
              <section className="mb-10" aria-labelledby="quick-access-heading">
                <h3 id="quick-access-heading" className="text-lg sm:text-xl font-bold text-navy-700 mb-4">
                  Quick Access
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {quickAccessItems.map((item) => (
                    <a
                      key={item.key}
                      href={item.resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleResourceOpen(item.resource)}
                      aria-label={`Open ${item.label} (opens in new tab)`}
                      className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3.5 min-h-[44px] transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-xl shrink-0" aria-hidden="true">
                        {item.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-bold text-navy-700 text-sm leading-tight truncate">{item.label}</span>
                        <span className="block text-xs text-slate-500 truncate">{item.subtitle}</span>
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Recently Visited */}
            {visibleRecents.length > 0 && (
              <section className="mb-10" aria-labelledby="recent-heading">
                <h3 id="recent-heading" className="text-lg sm:text-xl font-bold text-navy-700 mb-4">
                  Recently Visited
                </h3>
                <div className="flex flex-wrap gap-3">
                  {visibleRecents.map((r) => (
                    <a
                      key={r.id ?? r.slug ?? r.url}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleResourceOpen(r)}
                      aria-label={`Open ${r.title} (opens in new tab)`}
                      className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 min-h-[44px] text-sm font-medium text-slate-700 transition hover:shadow-md hover:-translate-y-0.5 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                    >
                      <span aria-hidden="true">{r.icon || '🔗'}</span> {r.title}
                    </a>
                  ))}
                </div>
              </section>
            )}

            {/* Legal Help cross-link */}
            <section className="mb-10" aria-labelledby="legal-help-heading">
              <h3 id="legal-help-heading" className="sr-only">Legal Help</h3>
              <Link
                to="/find-legal-help"
                className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-2xl shrink-0" aria-hidden="true">
                  ⚖️
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-base sm:text-lg font-bold text-navy-700">Need Legal Help?</h4>
                  <p className="text-sm text-slate-500">
                    Find licensed immigration attorneys and law firms near you.
                  </p>
                </div>
                <div className="font-semibold text-sm text-blue-600 whitespace-nowrap shrink-0">
                  Find an Attorney <span aria-hidden="true">→</span>
                </div>
              </Link>
            </section>

            {/* Category sections */}
            {showEmptyState ? (
              <div className="bg-white rounded-2xl p-8 text-center border border-slate-200">
                <div className="text-5xl mb-3" aria-hidden="true">🔍</div>
                <p className="text-navy-700 font-semibold">No resources match your search.</p>
              </div>
            ) : (
              groupedResources.map(({ category, resources }) => (
                <section key={category.id} className="mb-10" aria-labelledby={`cat-heading-${category.id}`}>
                  <h3
                    id={`cat-heading-${category.id}`}
                    className="text-lg sm:text-xl font-bold text-navy-700 mb-4 flex items-center gap-2"
                  >
                    <span aria-hidden="true">{category.icon}</span> {category.name}
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                    {resources.map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        category={categoryById.get(resource.category_id)}
                        isLoggedIn={!!user}
                        isSaved={savedIds.has(resource.id)}
                        onToggleSave={() => toggleSave(resource)}
                        onOpen={() => handleResourceOpen(resource)}
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </>
        )}
      </main>

      <footer className="bg-white border-t border-slate-200 py-6 mt-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-slate-500 max-w-3xl mx-auto">
            Not affiliated with USCIS or any government agency. Links go to official third-party sites — always verify
            information directly with the source.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ResourcesCenter;
